import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const AppleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-3.13 1.71-3.95-.24-.81-1.64-.9-2.38-.9-.73.03-1.94.97-2.6 1.77-.58.71-1.4 2.09-1.4 2.95 0 .1.02.2.05.27.1.4 1.17.92 2.62.92z"/>
  </svg>
);

const PlayIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5.14v14.72l11-7.36L8 5.14z"/>
  </svg>
);

// Стили
const Modal = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #0a0a0a 0%, #111118 100%);
  padding: 3rem 2.5rem;
  border-radius: 32px;
  max-width: 520px;
  width: 100%;
  text-align: center;
  border: 1px solid rgba(0, 245, 255, 0.15);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: radial-gradient(circle at 50% 0%, rgba(0, 245, 255, 0.08), transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 480px) {
    padding: 2.5rem 1.8rem;
    border-radius: 28px;
  }
`;

const Title = styled.h2`
  font-size: clamp(2rem, 6vw, 2.6rem);
  font-weight: 900;
  margin: 0 0 0.6rem;
  background: linear-gradient(120deg, #ffffff, #00f5ff, #ffffff);
  background-size: 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientFlow 10s ease infinite;

  @keyframes gradientFlow {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.88;
  margin: 0 0 2.5rem;
  line-height: 1.6;
  color: #e0e0ff;
  font-weight: 400;
`;

const StoreButtons = styled.div`
  display: flex;
  gap: 1.2rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const StoreLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  padding: 1.1rem 1.6rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 180px;
  backdrop-filter: blur(8px);

  &:hover {
    background: rgba(0, 245, 255, 0.15);
    border-color: #00f5ff;
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 245, 255, 0.2);
  }

  svg {
    width: 26px;
    height: 26px;
  }
`;

const CloseLink = styled(motion.button)`
  background: none;
  border: none;
  color: #888;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 2rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.25s ease;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

// Анимации
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  },
  exit: { opacity: 0, transition: { duration: 0.3 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const DownloadModal = ({ onClose }) => {
  const { t } = useTranslation();

  return (
    <Modal
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={onClose}
    >
      <ModalContent onClick={e => e.stopPropagation()}>
        <motion.div variants={itemVariants}>
          <Title>{t('modal_title')}</Title>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Subtitle>{t('modal_subtitle')}</Subtitle>
        </motion.div>

        <motion.div variants={itemVariants}>
          <StoreButtons>
            <StoreLink
              href="https://apps.apple.com/app/plano-ai/id123456789"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              <AppleIcon />
              {t('modal_appstore')}
            </StoreLink>

            <StoreLink
              href="https://play.google.com/store/apps/details?id=com.plano.ai"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              <PlayIcon />
              {t('modal_googleplay')}
            </StoreLink>
          </StoreButtons>
        </motion.div>

        <motion.div variants={itemVariants}>
          <CloseLink onClick={onClose} whileTap={{ scale: 0.95 }}>
            {t('modal_later')}
          </CloseLink>
        </motion.div>
      </ModalContent>
    </Modal>
  );
};

export default DownloadModal;